import React, {useMemo} from 'react'
import {useSelector} from 'react-redux'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import {getLanguageCode} from 'modules/meta/selectors'
import {times} from 'ramda'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Box} from '@vio/atlas/helpers/Box'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {linariaMq} from '@vio/atlas/utils/breakpoints'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {useGetReviewsQuery} from '@vio/core/src/trustpilotWidget/services/trustpilotApi'

import {HomePageSectionTitle} from '../commonStyles'
import {HighlightedTitle} from '../HighlightedTitle'
import {TrustpilotDetails} from '../TrustpilotDetails'
import {TrustPilotReviewItem} from './TrustPilotReviewItem'

const Wrapper = styled.section`
  background: linear-gradient(180deg, #fff 0%, #f0ecf5 100%);
  padding: calc(${cssTheme.layout.spacing.s800} * 2) 0;
  padding-bottom: 0;
  ${linariaMq.desktopXs} {
    padding: calc(${cssTheme.layout.spacing.s800} * 3) 0;
    padding-bottom: 104px;
  }
`

const EmptyBox = styled.section`
  padding: ${cssTheme.layout.spacing.s800};
`

const ReviewsWrapper = styled.div`
  overflow: hidden;
  padding: ${cssTheme.layout.spacing.s600};
`

const ReviewsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${cssTheme.layout.spacing.s500};
  @keyframes scroll {
    0% {
      transform: translateX(0);
    }
    100% {
      transform: translateX(-100%);
    }
  }

  will-change: transform;
  &:not(.--stop-animation) {
    animation: scroll 120s linear infinite;
  }
`

const HeaderWrapper = styled.div`
  padding-left: ${cssTheme.layout.spacing.s500};
  padding-right: ${cssTheme.layout.spacing.s500};
  padding-bottom: ${cssTheme.layout.spacing.s500};

  ${linariaMq.desktopXs} {
    padding-bottom: ${cssTheme.layout.spacing.s900};
  }
`

const RatingDetailsWrapper = styled.div`
  display: flex;
  flex-direction: row-reverse;
  padding-left: calc(50% - ${cssTheme.layout.maxPageWidth} / 2);
`

const TrustPilotReviewContainer = styled.div`
  -webkit-box-align: stretch;
  align-items: stretch;
`

const RatingDetailsCard = styled.div`
  ${linariaMq.desktopSm} {
    background-color: ${cssTheme.colors.background.neutral.primary};
    padding: 80px ${cssTheme.layout.spacing.s700};
    border-radius: ${cssTheme.layout.radius.xxl};
    width: 290px;
    height: 418px;
    box-shadow: ${cssTheme.shadows.fixed};
    z-index: 1;
  }
`

const SocialProofDetails = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  overflow: hidden;
  padding: ${cssTheme.layout.spacing.s500} 0;

  ${linariaMq.desktopSm} {
    flex-direction: row;
  }
`

interface Props {
  // If true, the animation of the reviews will be stopped. This is used for taking storybook snapshots.
  stopAnimation?: boolean
}

export const SocialProofSection: React.FC<Props> = ({stopAnimation}) => {
  const languageCode = useSelector(getLanguageCode)
  const {data} = useGetReviewsQuery({language: languageCode})
  const {isDesktop} = useDeviceLayout()

  const reviews = useMemo(
    () =>
      data?.reviews?.length && (
        <ReviewsGroup className={cx(stopAnimation && '--stop-animation')}>
          {data.reviews.map(review => (
            <TrustPilotReviewContainer key={review.id}>
              <TrustPilotReviewItem review={review} />
            </TrustPilotReviewContainer>
          ))}
        </ReviewsGroup>
      ),
    [data, stopAnimation]
  )

  if (!reviews) return <EmptyBox />

  return (
    <Wrapper>
      <HeaderWrapper>
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
        >
          <HomePageSectionTitle
            variant={isDesktop ? 'title4XL' : 'titleXL'}
            className="--center"
          >
            <HighlightedTitle source="SocialProofSection" />
          </HomePageSectionTitle>
          <Text tag="div" variant={isDesktop ? 'title3XL' : 'titleL'}>
            <FormattedMessageWrapper
              id="homePage.socialProofSubtitle"
              defaultMessage="(in a good way)"
            />
          </Text>
        </Box>
      </HeaderWrapper>
      <SocialProofDetails>
        <RatingDetailsWrapper>
          <RatingDetailsCard>
            <TrustpilotDetails section="SocialProof" />
          </RatingDetailsCard>
        </RatingDetailsWrapper>
        <ReviewsWrapper>
          <Box display="flex" id="trustpilot-reviews" gap="s500">
            {/* The reviews are duplicated to create an illusion of continuity for an animation,
                to ensure a seamless and continuous display of reviews. */}
            {times(() => reviews, stopAnimation ? 1 : 2)}
          </Box>
        </ReviewsWrapper>
      </SocialProofDetails>
    </Wrapper>
  )
}
